import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import LedgerTypeService from "../../../services/AccountingServices/LedgerTypeService";

const LedgerTypeForm = ({ isOpen, toggle, selectedItem, refetch }) => {
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    category: Yup.string().required("Category is required"),
    description: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      id: selectedItem?.id || 0,
      name: selectedItem?.name || "",
      category: selectedItem?.category || "",
      description: selectedItem?.description || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (values.id) {
          await LedgerTypeService.update(values.id, values);
          toast.success("Ledger Type updated successfully");
        } else {
          await LedgerTypeService.create(values);
          toast.success("Ledger Type created successfully");
        }
        refetch();
        toggle();
      } catch (error) {
        console.error("Submission error:", error);
        toast.error(error.response?.data?.message || "Failed to save ledger type");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="border-bottom-0">
        {selectedItem ? "Edit Ledger Type" : "Add Ledger Type"}
      </ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody className="py-4">
          <Row>
            <Col lg={12}>
              <FormGroup>
                <Label for="name" className="fw-semibold small text-uppercase text-muted">
                  Name <span className="text-danger">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="e.g. Bank, Cash, Expenses"
                  className="form-control-lg border-2"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && !!formik.errors.name}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </FormGroup>
            </Col>
            <Col lg={12}>
              <FormGroup>
                <Label for="category" className="fw-semibold small text-uppercase text-muted">
                  Category <span className="text-danger">*</span>
                </Label>
                <Input
                  id="category"
                  name="category"
                  type="select"
                  className="form-select-lg border-2"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.category && !!formik.errors.category}
                >
                  <option value="">Select Category</option>
                  <option value="ASSETS">Assets</option>
                  <option value="LIABILITIES">Liabilities</option>
                  <option value="INCOME">Income</option>
                  <option value="EXPENSES">Expenses</option>
                </Input>
                <FormFeedback>{formik.errors.category}</FormFeedback>
              </FormGroup>
            </Col>
            <Col lg={12}>
              <FormGroup>
                <Label for="description" className="fw-semibold small text-uppercase text-muted">
                  Description
                </Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  rows={3}
                  placeholder="Briefly describe this ledger type"
                  className="form-control border-2"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="border-top-0 d-flex gap-2">
          <Button color="light" className="px-4" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" className="px-4" type="submit" disabled={loading}>
            {loading ? "Saving..." : selectedItem ? "Update Ledger Type" : "Create Ledger Type"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default LedgerTypeForm;
